import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { ViewType, DefaultViewport } from '@components-molecules/map/map-constants';
import eventBus from '@tools/event-bus';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import requests from '@tools/requests';
import {
  URL_XHR_PROPOSAL_SEND_EMAIL,
  URL_XHR_SELECTION_ITEMS_SORT,
} from '@tools/routes/routes';
import { trans } from '@elements/translation';
import Logger from '@tools/logger/logger';
import Marker from '@components-molecules/map/marker';
import UserAuth from '@tools/user/auth';
import { useCustomMarkerStore } from '@organisms/offer/selection-items/stores/useCustomMarkerStore';
import { SelectionItemEvent } from '@enums/event';
import type { SelectionItem } from '@models/selectionItem.ts';
import { apiCreateSelectionItemProposal, type RemoveSelectionItemParams } from '@api/selectionItem/apiSelectionItem';
import type { SelectionItemInitialAppState } from '@global-types/initialAppState';
import type { SerializedUser } from '@models/user.ts';
import type { SearchCommentTimeline } from '@models/searchCommentTimeline';
import type { GoogleMap, GoogleMapMarker } from '@global-types/googleMap.ts';
import type { AccountManager } from '@models/accountManager.ts';
import { apiArchiveProposal, apiUpdateProposal } from '@api/proposal/apiProposal';
import type { Preresize } from '@global-types/preresize.ts';

// Has to be global
let customMarker: GoogleMapMarker | null = null;

/* global google */
const useSelectionItemStore = defineStore('selection-item-store', () => {
  const initialAppState = window.selectionItemInitialAppState as SelectionItemInitialAppState;

  const activeItems = ref<SelectionItem[]>(initialAppState.active_items);
  const removedItems = ref<SelectionItem[]>(initialAppState.removed_items);
  const accountManagerIsOnline = ref<boolean>(initialAppState.is_account_manager_online);

  const unmentionablePipedriveUserIds = ref<number[]>(initialAppState.unmentionable_pipedrive_user_ids);

  const currentView = ref<ViewType>(ViewType.LIST);
  const itemScrollTo = ref<SelectionItem | null>(null);

  const map = ref<GoogleMap | null>(null);
  const markers = ref<Record<number, Marker>>({});

  const preresizes = computed<Record<string, Preresize>>(() => initialAppState.preresizes);

  const proposalCustomMarkerStore = useCustomMarkerStore();
  const { savedDealUdurMarker } = storeToRefs(proposalCustomMarkerStore);

  const initMap = (googleMap: HTMLDivElement, isMobileOrTablet: boolean): void => {
    Logger.debug('[MAP] Initiate the map');

    const options = {
      gestureHandling: 'cooperative',
      clickableIcons: false,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      zoomControl: false,
      styles: [
        { featureType: 'poi', stylers: [ { visibility: 'off' } ] },
        { featureType: 'transit.station.bus', stylers: [ { visibility: 'off' } ] },
      ],
    };

    if (isMobileOrTablet) {
      options.gestureHandling = 'greedy';
    }

    map.value = new google.maps.Map(googleMap, options);
  };

  const setView = (view: ViewType): void => {
    currentView.value = view;
  };

  const sortedItems = computed<SelectionItem[]>(() => activeItems.value
    .sort((a, b) => a.display_order - b.display_order || a.created_at.unix - b.created_at.unix));

  const sortedRemovedSelectionItems = computed(() => removedItems.value.sort((a, b) => {
    const aUnix = a.removed_at?.unix ?? 0;
    const bUnix = b.removed_at?.unix ?? 0;
    return bUnix - aUnix;
  }));

  const firstItemId = computed<number | undefined>(() => sortedItems.value[0]?.id);

  const accountManager = computed<AccountManager | null>(() => initialAppState.account_manager);
  const organizationSize = computed<number>(() => initialAppState.organization_size);

  const zoomIn = (): void => {
    if (!map.value) {
      return;
    }

    map.value.setZoom((map.value.getZoom() ?? 0) + 1);
  };

  const zoomOut = (): void => {
    if (!map.value) {
      return;
    }

    map.value.setZoom((map.value.getZoom() ?? 0) - 1);
  };

  const addMapMarker = (item: SelectionItem): void => {
    if (!map.value || markers.value[item.id]) return;

    const addressCoordinates = {
      lat: item.offer.address.latitude,
      lng: item.offer.address.longitude,
    };

    markers.value[item.id] = new Marker(
      item.id,
      addressCoordinates,
      item.offers.reduce((total, officeOffer) => total + officeOffer.price, 0),
      map.value,
    );
  };

  const setMapCustomMarker = (): void => {
    if (!map.value) {
      return;
    }

    if (customMarker) {
      customMarker.setMap(null);
    }

    if (!savedDealUdurMarker.value) {
      return;
    }

    const { latitude, longitude } = savedDealUdurMarker.value;

    const markerOptions = {
      position: new google.maps.LatLng(latitude, longitude),
      map: map.value,
    };

    customMarker = new google.maps.Marker(markerOptions);

    customMarker.addListener('click', () => {
      proposalCustomMarkerStore.open();
    });
  };

  const fitMapToMarkers = (): void => {
    if (!map.value) return;

    let bounds = new google.maps.LatLngBounds();

    if (activeItems.value.length) {
      activeItems.value.forEach((proposal) => {
        const { latitude, longitude } = proposal.offer.address;
        bounds.extend(new google.maps.LatLng(latitude, longitude));
      });
    } else {
      bounds = new google.maps.LatLngBounds(
        new google.maps.LatLng(DefaultViewport.SOUTH, DefaultViewport.WEST),
        new google.maps.LatLng(DefaultViewport.NORTH, DefaultViewport.EAST),
      );
    }

    if (savedDealUdurMarker.value) {
      const { latitude, longitude } = savedDealUdurMarker.value;
      bounds.extend(new google.maps.LatLng(latitude, longitude));
    }

    map.value.fitBounds(
      bounds,
      window.BREAKPOINTS.isMobile ? 5 : 200,
    );
  };

  const removeMapMarker = (itemId: number): void => {
    if (!map.value) return;

    markers.value[itemId]?.remove();
    delete markers.value[itemId];

    fitMapToMarkers();
  };

  const addMapMarkers = (): void => {
    setMapCustomMarker();

    activeItems.value.forEach((proposal) => {
      addMapMarker(proposal);
    });

    fitMapToMarkers();

    const transitLayer = new google.maps.TransitLayer();
    transitLayer.setMap(map.value);
  };

  const removeSelectionItem = ({ selectionItemId, refusalReasons, refusedByUser = true }: RemoveSelectionItemParams): void => {
    const itemToRemove = activeItems.value.find(
      (item) => item.id === selectionItemId,
    );

    if (!itemToRemove) {
      return;
    }

    const itemId = itemToRemove.id;

    itemToRemove.refusal_reasons = refusalReasons ?? [];

    itemToRemove.removed_by = UserAuth.isImpersonated()
      ? {
        ...itemToRemove.user as SerializedUser,
        firstname: accountManager.value?.firstname ?? itemToRemove.user.firstname,
      }
      : itemToRemove.user;

    itemToRemove.is_removed = true;

    const now = new Date();

    itemToRemove.removed_at = {
      iso: now.toISOString(),
      unix: now.getTime(),
    };

    if (refusedByUser) {
      removedItems.value = [ ...removedItems.value, itemToRemove ];
    }

    activeItems.value = activeItems.value.filter(
      (item) => item.id !== itemId,
    );

    removeMapMarker(itemId);
  };

  const restoreSelectionItem = ({ itemId, order }: { itemId: number, order: number }): void => {
    const itemToRestore = removedItems.value.find(
      (item) => item.id === itemId,
    );

    if (!itemToRestore) {
      return;
    }

    itemToRestore.is_removed = false;
    itemToRestore.display_order = order;

    activeItems.value = [ ...activeItems.value, itemToRestore ];

    removedItems.value = removedItems.value.filter(
      (item) => item.id !== itemId,
    );

    addMapMarker(itemToRestore);
    fitMapToMarkers();
  };

  const setSelectionItemAsFavorite = ({
    offerHashId,
    isRemoved,
    newIsFavorite,
  }: {
    offerHashId: string,
    isRemoved: boolean,
    newIsFavorite: boolean,
  }) => {
    if (!isRemoved) {
      const itemToUpdateIndex = activeItems.value.findIndex(
        (item) => item.offer.hash_id === offerHashId,
      );

      activeItems.value[itemToUpdateIndex].is_favorite = newIsFavorite;

      return;
    }

    const itemToUpdateIndex = removedItems.value.findIndex(
      (item) => item.offer.hash_id === offerHashId,
    );

    removedItems.value[itemToUpdateIndex].is_favorite = newIsFavorite;
  };

  const archiveProposal = async (selectionItem: SelectionItem): Promise<void> => {
    if (!selectionItem.proposal) {
      return;
    }

    const archivedSuccessfully = await apiArchiveProposal(selectionItem.proposal.id);

    if (!archivedSuccessfully) {
      return;
    }

    if (!selectionItem.is_favorite && !selectionItem.deal) {
      activeItems.value = activeItems.value.filter((item) => item.id !== selectionItem.id);

      flashBagSuccess(trans('selection_item.flashbag.archived'));
      return;
    }

    const itemToUpdateIndex = activeItems.value.findIndex(
      (item) => item.id === selectionItem.id,
    );

    if (itemToUpdateIndex === -1) {
      return;
    }

    const itemToUpdate = activeItems.value[itemToUpdateIndex];

    if (!itemToUpdate.proposal) {
      return;
    }

    activeItems.value[itemToUpdateIndex] = {
      ...itemToUpdate,
      proposal: {
        id: itemToUpdate.proposal.id,
        comment: itemToUpdate.proposal.comment,
        is_active: false,
      },
    };

    flashBagSuccess(trans('selection_item.flashbag.archived'));
  };

  const createProposalComment = async ({
    selectionItemId,
    comment,
  }: {
    selectionItemId: number,
    comment: string
  }): Promise<boolean> => {
    const data = await apiCreateSelectionItemProposal(selectionItemId, { comment });

    if (!data) {
      return false;
    }

    const itemToUpdateIndex = activeItems.value.findIndex(
      (item) => item.id === selectionItemId,
    );

    const itemToUpdate = activeItems.value[itemToUpdateIndex];

    activeItems.value[itemToUpdateIndex] = {
      ...itemToUpdate,
      proposal: {
        id: data.proposalId,
        comment,
        is_active: true,
      },
    };

    flashBagSuccess(trans('selection_item.review.flashbag_success'));

    return true;
  };

  const updateProposalComment = async ({
    proposalId,
    comment,
  }: {
    proposalId: number,
    comment: string,
  }): Promise<boolean> => {
    const updatedSuccessfully = await apiUpdateProposal(proposalId, { comment });

    if (!updatedSuccessfully) {
      flashBagError(trans('selection_item.review.flashbag_error'));
      return false;
    }

    const itemToUpdateIndex = activeItems.value.findIndex(
      (item) => item.proposal?.id === proposalId,
    );

    const itemToUpdate = activeItems.value[itemToUpdateIndex];

    activeItems.value[itemToUpdateIndex] = {
      ...itemToUpdate,
      proposal: {
        id: itemToUpdate.proposal?.id ?? 0,
        comment,
        is_active: true,
      },
    };

    flashBagSuccess(trans('selection_item.review.flashbag_success'));

    return true;
  };

  const reorderItems = async (orderedItemIds: number[]): Promise<void> => {
    const apiFormattedItems = orderedItemIds
      .map((selectionItemId, order) => ({
        selectionItemId,
        order,
      }));

    try {
      await requests.post(URL_XHR_SELECTION_ITEMS_SORT, apiFormattedItems);
    } catch (e) {
      flashBagError(trans('selection_item.sort.flashbag_error'));
      Logger.error(e);
    }
  };

  const sendEmailWithLastProposal = async (): Promise<void> => {
    try {
      await requests.post(URL_XHR_PROPOSAL_SEND_EMAIL);
      flashBagSuccess(trans('selection_item.send_email.flashbag_success'));
    } catch (error) {
      flashBagError();
    }
  };

  const addSearchCommentTimeline = async ({ itemId, author }: { itemId: number, author: string}): Promise<void> => {
    let itemToUpdateIndex = activeItems.value.findIndex(
      (item) => item.id === itemId,
    );

    if (itemToUpdateIndex > -1) {
      if (!Object.values(activeItems.value[itemToUpdateIndex].search_comment.authors).includes(author)) {
        activeItems.value[itemToUpdateIndex].search_comment.authors.push(author);
      }

      activeItems.value[itemToUpdateIndex].search_comment.counter += 1;

      return;
    }

    itemToUpdateIndex = removedItems.value.findIndex(
      (item) => item.id === itemId,
    );

    if (!removedItems.value[itemToUpdateIndex].search_comment.authors.includes(author)) {
      removedItems.value[itemToUpdateIndex].search_comment.authors.push(author);
    }

    removedItems.value[itemToUpdateIndex].search_comment.counter += 1;
  };

  const updateLastCommentTimeline = async ({
    itemId,
    searchCommentTimeline,
  }: {
    itemId: number;
    searchCommentTimeline: SearchCommentTimeline;
  }): Promise<void> => {
    let itemToUpdateIndex = activeItems.value.findIndex(
      (item) => item.id === itemId,
    );

    if (itemToUpdateIndex > -1) {
      activeItems.value[itemToUpdateIndex].search_comment.last_comment = searchCommentTimeline;

      return;
    }

    itemToUpdateIndex = removedItems.value.findIndex(
      (item) => item.id === itemId,
    );

    removedItems.value[itemToUpdateIndex].search_comment.last_comment = searchCommentTimeline;
  };

  const eventBusListen = () => {
    eventBus.addEventListener(SelectionItemEvent.SCROLL_TO, (event: Event) => {
      itemScrollTo.value = event.detail;
    });
  };

  const eventBusOff = () => {
    eventBus.removeEventListener(SelectionItemEvent.SCROLL_TO);
  };

  watch(savedDealUdurMarker, () => {
    setMapCustomMarker();
    fitMapToMarkers();
  });

  return {
    activeSelectionItems: sortedItems,
    removedSelectionItems: sortedRemovedSelectionItems,
    unmentionablePipedriveUserIds,
    itemScrollTo,
    currentView,
    firstItemId,
    accountManager,
    organizationSize,
    preresizes,
    setView,
    initMap,
    zoomIn,
    zoomOut,
    addMapMarkers,
    removeMapMarker,
    restoreSelectionItem,
    setSelectionItemAsFavorite,
    updateProposalComment,
    addSearchCommentTimeline,
    updateLastCommentTimeline,
    eventBusListen,
    eventBusOff,
    reorderItems,
    sendEmailWithLastProposal,
    removeSelectionItem,
    archiveProposal,
    accountManagerIsOnline,
    createProposalComment,
  };
});

export default useSelectionItemStore;
